import { useState, useCallback, useMemo, useEffect, useRef } from "react";

import type { Dayjs } from "dayjs";
import type { Options } from "highcharts";
import type { SeriesLineOptions } from "highcharts";
import styled from "styled-components";

import { SWReactIcons } from "@similarweb/icons";
import { LEGEND_POSITIONS, LineGraph } from "@similarweb/pro-logic-components";
import { colorsPalettes, elevation } from "@similarweb/styles";
import { Banner, IconButton } from "@similarweb/ui-components";
import { Flex, Text } from "@similarweb/ui-components/foundations";

import { getDurationSwitcherOptions } from "app/common/keyword-research/keywords-overview/trendOverTimeConsts";
import {
    getUpdatedGranularitySwitcherItems,
    isDurationSwitcherLocked,
    resolveGraphGranularityValue,
} from "app/common/keyword-research/keywords-overview/trendOverTimeTimeframeHelpers";
import type { ISwitcherItem } from "app/common/keyword-research/keywords-overview/trendOverTimeTypes";
import { WidgetErrorFallback } from "app/components/error-boundary-no-data/ErrorBoundaryEmptyWidget";
import { WidgetActionsItemWrapper } from "app/components/React/WidgetContainer/styledComponents";
import { WidgetContainer } from "app/components/React/WidgetContainer/WidgetContainer";
import { THRESHOLD_FOR_KEYWORD_RESEARCH_VALUE } from "app/filters/const";
import { translate } from "app/filters/i18nFilter";
import { minAbbrNumberFilter } from "app/filters/ngFilters";
import { areArraysEqualIgnoringOrder } from "app/helpers/arrays";
import { useSearchAdvertisingDataAndParams } from "app/pages/digital-marketing/ad-intelligence/common/hooks/useSearchAdvertisingDataAndParams";
import {
    trackSwitcherItemClick,
    useSwitcherItems,
} from "app/pages/keyword-research/keyword-analysis/pages/keywords-overview/trend-over-time/trendOverTimeUtils";
import { getChartConfig } from "app/pages/keyword-research/website-analysis/pages/website-keywords/table/clicks-over-time-graph/chartConfig";
import { graphTooltip } from "app/pages/keyword-research/website-analysis/pages/website-keywords/table/clicks-over-time-graph/GraphTooltip";
import type {
    ITransformedDataPoint,
    SelectedKeywords,
} from "app/pages/keyword-research/website-analysis/pages/website-keywords/table/clicks-over-time-graph/types";
import { SwitcherGranularityContainer } from "app/pages/website-analysis/components/SwitcherGranularityContainer";
import type { BasicDurationValues, IDurationObject } from "app/services/DurationService";
import DurationService, { BasicDurations } from "app/services/DurationService";
import { openHooksModalV5 } from "app/services/ModalService";
import { allTrackers } from "app/services/track/track";
import { GranularityNames } from "app/UtilitiesAndConstants/Constants/Moment";
import { SourceTypeMap } from "app/UtilitiesAndConstants/Constants/SourceTypes";
import type { ValueOf } from "pro-features/types";

import { useClicksOverTimeGraphData } from "../../hooks/useClicksOverTimeGraphData";

import { getDefaultGraphDuration } from "./utils";

const getLegendHeaders = () => [
    translate("keywordanalysis.organic2.clicks.over.time.domain"),
    translate("website.keywords.2.0.table.graph.legend.clicks.title"),
];

type ErrorFallbackWrapperProps = {
    error: Error;
    resetErrorBoundary?: () => void;
    height?: string;
};

const ErrorFallbackWrapper = ({ error, resetErrorBoundary, height = "340px" }: ErrorFallbackWrapperProps) => (
    <Flex
        height={height}
        borderRadius="6px"
        justifyContent="center"
        background={colorsPalettes.carbon[0]}
        boxShadow={elevation.Z1}
    >
        <WidgetErrorFallback
            error={error}
            resetErrorBoundary={resetErrorBoundary ?? (() => window.location.reload())}
        />
    </Flex>
);

const BannerStyled = styled(Banner)`
    margin: 0 24px 24px;
    padding: 14px 12px 14px 16px;
    background-color: ${colorsPalettes.bluegrey[200]};
`;

const WidgetContainerWrapper = styled(WidgetContainer)`
    box-shadow: none;
`;

export const WebsiteKeywordsPage2Graph = ({
    selectedRowsKeywords,
    onCloseButtonClick,
    shouldPresentLegend = true,
    shouldPresentRefreshButton = true,
}: {
    selectedRowsKeywords: SelectedKeywords;
    onCloseButtonClick?: () => void;
    shouldPresentLegend?: boolean;
    shouldPresentRefreshButton?: boolean;
}) => {
    const { duration, selectedPageTab, comparedDuration } = useSearchAdvertisingDataAndParams();
    const isCompared = !!comparedDuration;
    const componentHeight = isCompared ? "416px" : "340px";
    const loaderHeight = isCompared ? 316 : 240;
    const durationData = DurationService.getDurationData(duration);
    const rawDurationData: IDurationObject<Dayjs> = durationData.raw;
    const [graphKeywords, setGraphKeywords] = useState<SelectedKeywords>(selectedRowsKeywords);
    const [graphDuration, setGraphDuration] = useState<ValueOf<typeof BasicDurations>>(
        getDefaultGraphDuration(duration, BasicDurations.LAST_MONTH),
    );
    const [graphGranularity, setGraphGranularity] = useState<GranularityNames>(GranularityNames.WEEKLY);

    const { graphData, handleLegendToggle, isLoading, error } = useClicksOverTimeGraphData(
        graphKeywords,
        graphGranularity,
        graphDuration,
        durationData,
    );
    const legendDataAdapter = (legendData: SeriesLineOptions[]) =>
        legendData.filter(
            (item) => (item as unknown as ITransformedDataPoint).isTotal === false,
        ) as unknown as ITransformedDataPoint[];

    const GraphTooltipFormatter = useMemo(
        () => graphTooltip(graphData, graphGranularity, rawDurationData, SourceTypeMap[selectedPageTab] as string),
        [graphData, graphGranularity, rawDurationData, selectedPageTab],
    );
    const legendValueFilter = (_value: number, legendData: ITransformedDataPoint) => {
        const roundedValue = Math.round(legendData.total / 10) * 10;
        return roundedValue === 0
            ? `<${THRESHOLD_FOR_KEYWORD_RESEARCH_VALUE}`
            : (minAbbrNumberFilter()(roundedValue, THRESHOLD_FOR_KEYWORD_RESEARCH_VALUE) as string);
    };

    const chartConfig: Options = getChartConfig(
        graphGranularity,
        duration === BasicDurations.LAST_TWENTY_EIGHT_DAYS,
        graphDuration,
        GraphTooltipFormatter,
    );
    const onDurationSwitcherItemClick =
        (switcherItems: ISwitcherItem[], selectedGranularity: GranularityNames) => (selectedIndex: number) => {
            const selectedItemValue = switcherItems[selectedIndex].value as BasicDurationValues;
            if (isDurationSwitcherLocked(selectedItemValue)) {
                openHooksModalV5("keywordResearch");
                return;
            }
            trackSwitcherItemClick(selectedItemValue);
            setGraphDuration(selectedItemValue);
            const graphGranularityForUpdate = resolveGraphGranularityValue(selectedItemValue, selectedGranularity);
            if (graphGranularityForUpdate) {
                setGraphGranularity(graphGranularityForUpdate);
            }
        };
    const onGranularitySwitcherItemClick = (switcherItems: ISwitcherItem[]) => (selectedIndex: number) => {
        const selectedItemValue = switcherItems[selectedIndex].value as GranularityNames;
        trackSwitcherItemClick(selectedItemValue);
        setGraphGranularity(selectedItemValue);
    };
    const { switcherItems: granularitySwitcherItems } = useSwitcherItems(
        undefined,
        graphDuration,
        getUpdatedGranularitySwitcherItems,
    );
    const { switcherItems: durationSwitcherItems } = useSwitcherItems(getDurationSwitcherOptions(duration));
    const switcherDuration = {
        items: durationSwitcherItems,
        onItemClick: onDurationSwitcherItemClick(durationSwitcherItems, graphGranularity),
        initialSelectedItem: durationSwitcherItems.findIndex((item) => item.value === graphDuration),
    };

    const switcherGranularity = {
        items: granularitySwitcherItems,
        onItemClick: onGranularitySwitcherItemClick(granularitySwitcherItems),
        initialSelectedItem: granularitySwitcherItems.findIndex((item) => item.value === graphGranularity),
    };

    const handleRefreshButtonClick = useCallback(() => {
        allTrackers.dispatchTrackEvent(
            "Button",
            "click",
            `Clicks Over Time Graph/Refresh button clicked/${selectedRowsKeywords.length}`,
            {
                selectedKeywordsLength: selectedRowsKeywords.length,
            },
        );
        setGraphKeywords(selectedRowsKeywords);
    }, [selectedRowsKeywords]);

    const isRefreshButtonEnabled = !areArraysEqualIgnoringOrder(selectedRowsKeywords, graphKeywords);
    const refreshButtonText = isRefreshButtonEnabled
        ? translate("website.keywords.2.0.table.graph.refresh.button.text")
        : translate("website.keywords.2.0.table.graph.refresh.button.disabled.text");

    const [hasAttemptedLoad, setHasAttemptedLoad] = useState(false);
    const prevIsLoading = useRef(isLoading);

    useEffect(() => {
        if (!prevIsLoading.current && isLoading) {
            setHasAttemptedLoad(false);
        }
        if (prevIsLoading.current && !isLoading) {
            setHasAttemptedLoad(true);
        }
        prevIsLoading.current = isLoading;
    }, [isLoading]);
    const shouldDisplayLoader = isLoading || (!hasAttemptedLoad && (!graphData || graphData.length === 0));
    const handleLegendSelectionTracking = (name: string, action: string) => {
        allTrackers.dispatchTrackEvent("Graph Filter", "click", `Clicks Over Time Graph/${name}/${action}`);
    };

    if (error) {
        const errorObj =
            error instanceof Error ? error : new Error(typeof error === "string" ? error : "Unknown error");
        return (
            <ErrorFallbackWrapper
                error={errorObj}
                height={componentHeight}
            />
        );
    }
    return (
        <WidgetContainerWrapper
            title={translate("website.keywords.2.0.table.graph.title")}
            isLoading={shouldDisplayLoader}
            loaderWidth={"100%"}
            loaderHeight={loaderHeight}
            height={componentHeight}
            closeButtonProps={{ isDisplayed: !!onCloseButtonClick, onClick: onCloseButtonClick }}
            errorComponentProps={{ fallbackComponent: ErrorFallbackWrapper }}
        >
            <Flex
                alignItems="center"
                margin="0 24px"
                padding="0 0 20px 0"
            >
                <WidgetActionsItemWrapper>
                    {[switcherDuration, switcherGranularity].map((dynamicSwitcher, index) => (
                        <SwitcherGranularityContainer
                            key={`dynamicSwitcher-${index}`}
                            itemList={dynamicSwitcher.items}
                            selectedIndex={dynamicSwitcher.initialSelectedItem}
                            onItemClick={dynamicSwitcher.onItemClick}
                            customClass="CircleSwitcher"
                            className="swichers-container"
                        />
                    ))}
                    {shouldPresentRefreshButton ? (
                        <IconButton
                            iconName="refresh"
                            type="flat"
                            isDisabled={!isRefreshButtonEnabled}
                            onClick={handleRefreshButtonClick}
                        >
                            {refreshButtonText}
                        </IconButton>
                    ) : null}
                </WidgetActionsItemWrapper>
            </Flex>
            {isCompared ? (
                <BannerStyled
                    title={
                        <Text
                            fontVariant="body1"
                            color={colorsPalettes.carbon[400]}
                        >
                            {translate("website.keywords.2.0.table.graph.pop.banner.text")}
                        </Text>
                    }
                    CustomIcon={
                        <SWReactIcons
                            iconName="alerts"
                            size="sm"
                        />
                    }
                />
            ) : null}
            <Flex
                margin="0 8px 0 24px"
                flexDirection="column"
            >
                <LineGraph
                    data={graphData}
                    graphConfig={chartConfig}
                    shouldPresentLegend={shouldPresentLegend}
                    shouldPresentValueAtLegend={true}
                    withoutTooltipsPadding={false}
                    legendHeaders={getLegendHeaders()}
                    isToggleAllEnabled={false}
                    legendValueFilter={legendValueFilter}
                    legendPosition={LEGEND_POSITIONS.RIGHT}
                    legendDataAdapter={legendDataAdapter}
                    customLegendTrackingHandler={handleLegendSelectionTracking}
                    componentNameForTracking={"Keyword Research/ Keywords/ Clicks Over Time chart/"}
                    legendsMaxWidth={250}
                    withMarkers={false}
                    legendGraphGap={14}
                    legendToggleHandlerCallback={handleLegendToggle}
                />
            </Flex>
        </WidgetContainerWrapper>
    );
};
